import {
  AdditiveBlending,
  BackSide,
  BufferAttribute,
  BufferGeometry,
  Color,
  DirectionalLight,
  HemisphereLight,
  Mesh,
  Points,
  ShaderMaterial,
  SphereGeometry,
  Vector3,
} from 'three';
import { Layer } from './Layer.js';

/**
 * The hour. Sky, fog, the last light off the horizon, the stars that are only
 * there once the eye has given up on the day, and the dust that proves the
 * air is between you and everything.
 */
export class Atmosphere extends Layer {
  static id = 'atmosphere';
  static order = 0;

  async build() {
    const { settings, palette, scene } = this.ctx;
    const cfg = settings.atmosphere;

    // ── Sky dome ─────────────────────────────────────────────────────────
    this.skyMat = new ShaderMaterial({
      side: BackSide,
      depthWrite: false,
      fog: false,
      uniforms: {
        uTop: { value: new Color() },
        uHorizon: { value: new Color() },
        uBottom: { value: new Color() },
        uTime: { value: 0 },
        uGlow: { value: 0.5 },
      },
      vertexShader: /* glsl */ `
        varying vec3 vDir;
        void main() {
          vDir = normalize(position);
          vec4 mv = modelViewMatrix * vec4(position, 1.0);
          gl_Position = projectionMatrix * mv;
        }
      `,
      fragmentShader: /* glsl */ `
        uniform vec3 uTop, uHorizon, uBottom;
        uniform float uTime, uGlow;
        varying vec3 vDir;

        float hash(vec3 p) {
          p = fract(p * 0.3183099 + vec3(0.1, 0.2, 0.3));
          p *= 17.0;
          return fract(p.x * p.y * p.z * (p.x + p.y + p.z));
        }

        void main() {
          float h = vDir.y;
          vec3 col = h > 0.0
            ? mix(uHorizon, uTop, pow(clamp(h, 0.0, 1.0), 0.62))
            : mix(uHorizon, uBottom, pow(clamp(-h, 0.0, 1.0), 0.45));

          // A band of remaining light low in the west.
          float west = smoothstep(0.55, 1.0, dot(normalize(vec3(vDir.x, 0.0, vDir.z)), vec3(-0.82, 0.0, 0.57)));
          float low  = exp(-abs(h) * 7.0);
          col += uHorizon * west * low * uGlow * 1.35;

          // Break up the banding the way real dusk does.
          col += (hash(floor(vDir * 420.0)) - 0.5) * 0.012;
          gl_FragColor = vec4(col, 1.0);
        }
      `,
    });
    const sky = new Mesh(new SphereGeometry(620, 48, 32), this.skyMat);
    sky.frustumCulled = false;
    sky.renderOrder = -100;
    this.sky = sky;
    this.group.add(sky);

    // ── Light ────────────────────────────────────────────────────────────
    this.hemi = new HemisphereLight(0x3a4d78, 0x161018, 1.25);
    this.group.add(this.hemi);

    this.sun = new DirectionalLight(0xffc38a, 1.5);
    this.sun.position.set(-240, 42, 165);
    this.group.add(this.sun);
    this.group.add(this.sun.target);

    // A cold fill from the opposite side so the shells read in the shadow.
    this.fill = new DirectionalLight(0x6f8fd0, 0.45);
    this.fill.position.set(180, 120, -140);
    this.group.add(this.fill);

    // ── Stars ────────────────────────────────────────────────────────────
    this.stars = this._makeStars(cfg.stars);
    this.group.add(this.stars);

    // ── Dust ─────────────────────────────────────────────────────────────
    this.motes = this._makeMotes(cfg.motes);
    this.group.add(this.motes);

    scene.fog.color.copy(palette.get('fog'));
    this._scratch = new Color();
  }

  _makeStars(count) {
    const pos = new Float32Array(count * 3);
    const size = new Float32Array(count);
    const tint = new Float32Array(count);
    const R = 560;
    for (let i = 0; i < count; i++) {
      // Weighted toward the upper hemisphere; you are, after all, descending.
      const u = Math.random();
      const y = Math.pow(u, 0.55) * 0.98 + 0.02;
      const r = Math.sqrt(Math.max(0, 1 - y * y));
      const a = Math.random() * Math.PI * 2;
      pos[i * 3] = Math.cos(a) * r * R;
      pos[i * 3 + 1] = y * R;
      pos[i * 3 + 2] = Math.sin(a) * r * R;
      size[i] = Math.pow(Math.random(), 3.2) * 4.2 + 0.5;
      tint[i] = Math.random();
    }
    const geo = new BufferGeometry();
    geo.setAttribute('position', new BufferAttribute(pos, 3));
    geo.setAttribute('aSize', new BufferAttribute(size, 1));
    geo.setAttribute('aTint', new BufferAttribute(tint, 1));

    this.starMat = new ShaderMaterial({
      transparent: true,
      depthWrite: false,
      fog: false,
      blending: AdditiveBlending,
      uniforms: { uTime: { value: 0 }, uOpacity: { value: 0 }, uScale: { value: 1 } },
      vertexShader: /* glsl */ `
        attribute float aSize;
        attribute float aTint;
        uniform float uTime, uScale;
        varying float vTint;
        varying float vTwinkle;
        void main() {
          vTint = aTint;
          vTwinkle = 0.65 + 0.35 * sin(uTime * (0.6 + aTint * 2.2) + aTint * 40.0);
          vec4 mv = modelViewMatrix * vec4(position, 1.0);
          gl_Position = projectionMatrix * mv;
          gl_PointSize = aSize * uScale * vTwinkle;
        }
      `,
      fragmentShader: /* glsl */ `
        uniform float uOpacity;
        varying float vTint;
        varying float vTwinkle;
        void main() {
          vec2 d = gl_PointCoord - 0.5;
          float r = length(d);
          float a = smoothstep(0.5, 0.0, r);
          a *= a;
          vec3 cold = vec3(0.72, 0.82, 1.0);
          vec3 warm = vec3(1.0, 0.88, 0.72);
          vec3 col = mix(cold, warm, vTint * vTint);
          gl_FragColor = vec4(col, a * uOpacity * vTwinkle);
        }
      `,
    });
    const pts = new Points(geo, this.starMat);
    pts.frustumCulled = false;
    pts.renderOrder = -90;
    return pts;
  }

  _makeMotes(count) {
    this.moteExtent = 46;
    const E = this.moteExtent;
    const pos = new Float32Array(count * 3);
    const seed = new Float32Array(count);
    for (let i = 0; i < count; i++) {
      pos[i * 3] = (Math.random() - 0.5) * 2 * E;
      pos[i * 3 + 1] = (Math.random() - 0.5) * 2 * E;
      pos[i * 3 + 2] = (Math.random() - 0.5) * 2 * E;
      seed[i] = Math.random();
    }
    const geo = new BufferGeometry();
    geo.setAttribute('position', new BufferAttribute(pos, 3));
    geo.setAttribute('aSeed', new BufferAttribute(seed, 1));

    this.moteMat = new ShaderMaterial({
      transparent: true,
      depthWrite: false,
      blending: AdditiveBlending,
      uniforms: {
        uTime: { value: 0 },
        uColor: { value: new Color(0xbfd2ff) },
        uWarm: { value: new Color(0xffd4a0) },
        uScale: { value: 1 },
        uOpacity: { value: 0.55 },
        uCamera: { value: new Vector3() },
        uExtent: { value: E },
      },
      vertexShader: /* glsl */ `
        attribute float aSeed;
        uniform float uTime, uScale, uExtent;
        uniform vec3 uCamera;
        varying float vSeed;
        varying float vFade;
        void main() {
          vSeed = aSeed;
          vec3 p = position;
          // Drift upward: ash from a day that already burned.
          p.y = mod(p.y + uTime * (0.35 + aSeed * 0.5) + uExtent, uExtent * 2.0) - uExtent;
          p.x += sin(uTime * 0.21 + aSeed * 31.0) * 1.6;
          p.z += cos(uTime * 0.17 + aSeed * 17.0) * 1.6;
          // Tile the cloud around the traveller so it is never left behind.
          p = mod(p - uCamera + uExtent, uExtent * 2.0) - uExtent + uCamera;

          vec4 mv = modelViewMatrix * vec4(p, 1.0);
          float dist = -mv.z;
          vFade = smoothstep(1.0, 6.0, dist) * (1.0 - smoothstep(uExtent * 0.55, uExtent, dist));
          gl_Position = projectionMatrix * mv;
          gl_PointSize = (14.0 + aSeed * 22.0) * uScale / max(dist, 1.0);
        }
      `,
      fragmentShader: /* glsl */ `
        uniform vec3 uColor, uWarm;
        uniform float uOpacity;
        varying float vSeed;
        varying float vFade;
        void main() {
          vec2 d = gl_PointCoord - 0.5;
          float a = smoothstep(0.5, 0.05, length(d));
          a *= a;
          vec3 col = mix(uColor, uWarm, step(0.78, vSeed));
          gl_FragColor = vec4(col, a * vFade * uOpacity);
        }
      `,
    });
    const pts = new Points(geo, this.moteMat);
    pts.frustumCulled = false;
    return pts;
  }

  update(dt, state) {
    const { palette, scene, camera } = this.ctx;
    const t = state.t;
    const cfg = this.ctx.settings.atmosphere;

    // The dome and the dust follow the traveller; the stars do not need to.
    this.sky.position.copy(camera.position);
    this.stars.position.copy(camera.position);

    this.skyMat.uniforms.uTop.value.copy(palette.get('sky'));
    this.skyMat.uniforms.uHorizon.value.copy(palette.get('horizon'));
    this.skyMat.uniforms.uBottom.value.copy(palette.get('void'));
    this.skyMat.uniforms.uTime.value = state.elapsed;
    this.skyMat.uniforms.uGlow.value = 0.95 - t * 0.6;

    this.starMat.uniforms.uTime.value = state.elapsed;
    this.starMat.uniforms.uOpacity.value = Math.pow(Math.min(1, t * 1.25), 1.6) * 0.95;
    this.starMat.uniforms.uScale.value = state.pixelRatio;

    this.moteMat.uniforms.uTime.value = state.elapsed;
    this.moteMat.uniforms.uCamera.value.copy(camera.position);
    this.moteMat.uniforms.uScale.value = state.pixelRatio;
    this.moteMat.uniforms.uColor.value.copy(palette.get('memory'));
    this.moteMat.uniforms.uWarm.value.copy(palette.get('lamp'));
    // Dust is a texture on the air, not a filter over the picture. A few
    // thousand additive sprites add up fast, so this stays low.
    this.moteMat.uniforms.uOpacity.value = 0.1 + t * 0.12;

    // Fog deepens, and so does the colour it is made of.
    scene.fog.color.copy(palette.get('fog'));
    scene.fog.density =
      cfg.fogDensity + (cfg.fogDensityNight - cfg.fogDensity) * Math.pow(t, 1.25);

    // Isidora is lit almost entirely by its own windows — see `Shaft`, which
    // keeps three lamps on the nearest openings. What is left here is only
    // what a shaft like this really has: a weak raking light from the last of
    // the sun, and the bounce off its own stone.
    this.sun.color.copy(palette.get('horizon')).lerp(this._scratch.copy(palette.get('lamp')), 0.5);
    this.sun.intensity = 0.85 * (1 - t * 0.8);
    this.sun.position.set(-240, 60 - t * 72, 165);

    this.hemi.color.copy(palette.get('sky'));
    this.hemi.groundColor.copy(palette.get('void'));
    // By the foot of the stair the sky has given up entirely and the square
    // is lit by its own lamps. Keeping any fill alive down there flattens
    // the whole ending into one brown wash.
    this.hemi.intensity = 0.78 - t * 0.62;

    this.fill.color.copy(palette.get('memory'));
    this.fill.intensity = 0.26 * (1 - t * 0.8);
  }

  stats() {
    return { fog: this.ctx.scene.fog.density.toFixed(4) };
  }
}
